import { Box, Text } from '@factorialco/deprecated-design-system'
import { RichTextEditor } from '@factorialco/f0-react/dist/experimental'
import {
  MeetingTalkingPointFragment,
  MeetingUpdateTalkingPointDocument,
} from 'generated/resources/graphql'
import { useDebounceValue } from 'usehooks-ts'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

interface ResultType {
  value: string | null
}

interface UpdateTalkingPoint {
  notes: string
}

interface Props {
  meetingId: string
  talkingPoint?: MeetingTalkingPointFragment
}

export function MeetingNotes({ meetingId, talkingPoint }: Props) {
  const { employee } = useCurrentSession()
  const { editorConfig } = useRichTextEditor()
  const [updateTalkingPoint] = useMutation(MeetingUpdateTalkingPointDocument, {
    extractorFn: (data) => data.oneOnOnesMutations.addOneononesTalkingPoint,
  })

  const form = useForm<UpdateTalkingPoint>({
    defaultValues: {
      notes: talkingPoint?.privateNotes || '',
    },
    onSubmit: async (data) => {
      const response = await updateTalkingPoint({
        variables: {
          meetingId,
          participantId: String(employee?.id),
          privateNotes: data.notes,
        },
      })

      const handled = handleResult(response, () => {
        // TODO: do something with form errors
      })

      if (handled.success) {
        form.resetForm({
          values: { notes: handled.data.talkingPoint?.privateNotes || '' },
        })
      }

      return handled
    },
  })

  const [debouncedNotes] = useDebounceValue(form.values.notes, 1000)

  useUnsafeEffect(() => {
    if (form.dirty) {
      form.handleSubmit()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedNotes])

  return (
    <Box gap="s24">
      <Text.Normal size="300" weight="semibold">
        {i18n.t('one_on_ones.meeting.notes.title')}
      </Text.Normal>

      <RichTextEditor
        {...editorConfig}
        initialEditorState={{
          content: form.values.notes,
        }}
        maxCharacters={10000}
        onChange={(result: ResultType) => {
          form.setFieldValue('notes', result.value ?? '')
        }}
        placeholder={i18n.t('one_on_ones.meeting.notes.placeholder')}
        title={i18n.t('one_on_ones.meeting.notes.title')}
        // TODO: This is a huge hack to show a submitting state to the user
        // this should be properly handled by the editor
        primaryAction={
          form.isSubmitting
            ? {
                action: {
                  label: i18n.t('one_on_ones.meeting.notes.autosaving'),
                  disabled: true,
                  onClick() {},
                  variant: 'neutral',
                },
              }
            : undefined
        }
      />
    </Box>
  )
}
